import dashboardImages from "~/pages/dashboard/assets";
import { getStyleIdFromBgAndClothing } from "~/utils/helpers";
import images, { StyleImageName } from "~/assets/img";
import camelCase from "lodash/camelCase";

export const DASHBOARD_STORAGE_KEYS = {
  DASHBOARD_PROGRESS: "DASHBOARD_PROGRESS",
  SURVEY_IDS: "SURVEY_IDS",
  JOB_ID: "JOB_ID",
  KEY_FEATURE: "KEY_FEATURE",
  STYLE_SELECTION: "STYLE_SELECTION",
  HEADSHOT_STYLE_SELECTION: "HEADSHOT_STYLE_SELECTION",
  VERIFY_RESULT: "VERIFY_RESULT",
  LIKED_PHOTOS: "LIKED_PHOTOS",
  KEY_FEATURE_2: "KEY_FEATURE_2",
};

export const DASHBOARD_PARAMS = {
  SESSION_ID: "session_id",
};

export const UPLOAD_DISCLAIMERS = [
  {
    id: 1,
    title: "Upload your best photos. Bad photos = bad results",
    type: "warning",
  },
  {
    id: 2,
    title: "Submit individual photos. Group shots are not accepted.",
    type: "warning",
  },
  {
    id: 3,
    title: "If you use old photos of yourself, you will look like your older self.",
    type: "warning",
  },
  {
    id: 4,
    title:
      "Keep in mind that recurring details like expressions, lighting, features (like glasses), etc  will be emphasised. ",
    type: "warning",
  },
  {
    id: 5,
    title: "No sunglasses / exposed skin / too much makeup",
    type: "bar",
  },
  {
    id: 6,
    title: "Avoid single expressions (i.e looking serious in all photos)",
    type: "bar",
  },
  {
    id: 7,
    title: "Avoid photos from same shoot / with same background",
    type: "bar",
  },
];

export interface UploadTips {
  images: { id: number; src: any }[];
  content: { id: number; desc: string }[];
}

export const UPLOAD_TIPS_BAD_PHOTOS: UploadTips = {
  images: [
    {
      id: 1,
      src: dashboardImages.badphoto1,
    },
    {
      id: 2,
      src: dashboardImages.badphoto2,
    },
    {
      id: 3,
      src: dashboardImages.badphoto5,
    },
  ],
  content: [
    {
      id: 1,
      desc: "No camera eye contact",
    },
    {
      id: 2,
      desc: "Group shots",
    },
    {
      id: 3,
      desc: "Awkward expression",
    },
    {
      id: 4,
      desc: "Low quality photo",
    },
    {
      id: 5,
      desc: "Too far/ too close",
    },
    {
      id: 6,
      desc: "Bad angle/ bad lighting",
    },
  ],
};

export const UPLOAD_TIPS_GOOD_PHOTOS: UploadTips = {
  images: [
    {
      id: 1,
      src: dashboardImages.uploadBody2,
    },
    {
      id: 2,
      src: dashboardImages.uploadHeadshot4,
    },
    {
      id: 3,
      src: dashboardImages.uploadHeadshot2,
    },
  ],
  content: [
    {
      id: 1,
      desc: "Make eye contact with the camera",
    },
    {
      id: 2,
      desc: "Personal photo",
    },
    {
      id: 3,
      desc: "Natural expression",
    },
    {
      id: 4,
      desc: "High quality photo",
    },
    {
      id: 5,
      desc: "Not too far/ not too close",
    },
    {
      id: 6,
      desc: "Nice angle/ good lighting",
    },
  ],
};

export const UPLOAD_TIPS_WARNING_PHOTOS = {
  images: [
    {
      id: 1,
      src: dashboardImages.warningphoto1,
    },
    {
      id: 2,
      src: dashboardImages.warningphoto2,
    },
    {
      id: 3,
      src: dashboardImages.warningphoto3,
    },
    {
      id: 4,
      src: dashboardImages.warningphoto4,
    },
  ],
  content: [
    {
      id: 1,
      desc: "Avoid single expressions (i.e. looking serious in all photos)",
    },
    {
      id: 2,
      desc: "Avoid photos from the same shoot / with same background",
    },
    {
      id: 3,
      desc: "If you use old photos of yourself, you will look like your older self",
    },
    {
      id: 4,
      desc: "Recurring details like expressions, lighting, features (like glasses), etc will be emphasized by AI.",
    },
  ],
};

export const GENDER = [
  { title: "Male", value: "man" },
  { title: "Female", value: "woman" },
];

export const MAX_FILES_UPLOAD = 10;

export const UPLOAD_HEADSHOT_SAMPLE_PHOTOS = [
  {
    order: 1,
    src: dashboardImages.uploadHeadshot1,
  },
  {
    order: 2,
    src: dashboardImages.uploadHeadshot2,
  },
  {
    order: 3,
    src: dashboardImages.uploadHeadshot3,
  },
  {
    order: 4,
    src: dashboardImages.uploadHeadshot4,
  },
  {
    order: 5,
    src: dashboardImages.uploadHeadshot5,
  },
  {
    order: 6,
    src: dashboardImages.uploadHeadshot6,
  },
  {
    order: 7,
    src: dashboardImages.uploadHeadshot7,
  },
];

export const UPLOAD_BODY_SAMPLE_PHOTOS = [
  {
    order: 1,
    src: dashboardImages.uploadBody1,
  },
  {
    order: 2,
    src: dashboardImages.uploadBody2,
  },
  {
    order: 3,
    src: dashboardImages.uploadBody3,
  },
  {
    order: 4,
    src: dashboardImages.uploadBody4,
  },
  {
    order: 5,
    src: dashboardImages.uploadBody5,
  },
  {
    order: 6,
    src: dashboardImages.uploadBody6,
  },
  {
    order: 7,
    src: dashboardImages.uploadBody7,
  },
];

export enum EUploadFileStatus {
  SUCCESS = "success",
  FAIL = "failed",
  DUPLICATE = "duplicate",
}

export interface StyleBackgroundProps {
  feId: number;
  title: string;
  value: string;
  previewImgFirstName: string;
  bgImg: string;
}

export interface StyleClothingProps {
  feId: number;
  bgIds: number[];
  label: string;
  value: string;
  previewImgLastName: string;
}

export interface HeadshotStyleProps {
  styleId: string;
  bg?: StyleBackgroundProps;
  clothing?: StyleClothingProps;
  maleImgName: StyleImageName;
  femaleImgName: StyleImageName;
  isSelected?: boolean;
}

export const STYLE_CLOTHING: StyleClothingProps[] = [
  {
    feId: 1,
    bgIds: [1, 2, 3, 4, 5, 6, 7],
    label: "Blue Business Suit",
    value: "Blue Business Suit", // same as BE define
    previewImgLastName: "blue-business-suit",
  },
  {
    feId: 2,
    bgIds: [1, 2, 3, 4, 5, 6, 7],
    label: "Grey Business Suit",
    value: "Grey Business Suit",
    previewImgLastName: "grey-business-suit",
  },
  {
    feId: 3,
    bgIds: [1, 2, 3, 4, 5, 6, 7, 15, 14, 11, 12, 13, 16, 17],
    label: "Black Sweater",
    value: "Black Sweater",
    previewImgLastName: "black-sweater",
  },
  {
    feId: 10,
    bgIds: [15, 11, 12, 13, 16, 17],
    label: "Grey Sweater",
    value: "Grey Sweater",
    previewImgLastName: "grey-sweater",
  },
  {
    feId: 4,
    bgIds: [1, 2, 3, 4, 5, 6, 7],
    label: "White Office Shirt",
    value: "White Office Shirt",
    previewImgLastName: "white-office-shirt",
  },
  {
    feId: 5,
    bgIds: [1, 2, 3, 4, 5, 6, 7],
    label: "Blue Office Shirt",
    value: "Blue Office Shirt",
    previewImgLastName: "blue-office-shirt",
  },

  {
    feId: 7,
    bgIds: [1, 2, 3, 4, 5, 6, 7, 11, 12, 13, 20],
    label: "Black T-Shirt",
    value: "Black T-Shirt",
    previewImgLastName: "black-t-shirt",
  },
  {
    feId: 14,
    bgIds: [20, 10, 9, 19],
    label: "Black T-Shirt and Leather Jacket",
    value: "Black T-Shirt and Leather Jacket",
    previewImgLastName: "black-t-shirt-and-leather-jacket",
  },
  {
    feId: 11,
    bgIds: [20, 10, 9, 19, 11, 12, 13],
    label: "White T-Shirt",
    value: "White T-Shirt",
    previewImgLastName: "white-t-shirt",
  },
  {
    feId: 15,
    bgIds: [20, 10, 9, 19],
    label: "White T-Shirt and Leather Jacket",
    value: "White T-Shirt and Leather Jacket",
    previewImgLastName: "white-t-shirt-and-leather-jacket",
  },
  {
    feId: 6,
    bgIds: [1, 2, 3, 4, 5, 6, 7],
    label: "Beige T-Shirt",
    value: "Beige T-Shirt",
    previewImgLastName: "beige-t-shirt",
  },
  {
    feId: 8,
    bgIds: [1, 2, 3, 4, 5, 6, 7],
    label: "Beige Casual Blazer",
    value: "Beige Casual Blazer",
    previewImgLastName: "beige-casual-blazer",
  },
  {
    feId: 9,
    bgIds: [1, 2, 3, 4, 5, 6, 7],
    label: "Blue Casual Blazer",
    value: "Blue Casual Blazer",
    previewImgLastName: "blue-casual-blazer",
  },
  {
    feId: 12,
    bgIds: [8],
    label: "White Lab Coat, Blue Scrubs",
    value: "White Lab Coat, Blue Scrubs",
    previewImgLastName: "white-lab-coat-blue-scrubs",
  },
  {
    feId: 13,
    bgIds: [8],
    label: "White Lab Coat, Green Scrubs",
    value: "White Lab Coat, Green Scrubs",
    previewImgLastName: "white-lab-coat-green-scrubs",
  },

  // {
  //   feId: 16,
  //   bgIds: [16, 17],
  //   label: "Graduation, Grey Sweater",
  //   value: "Graduation, Grey Sweater",
  //   previewImgLastName: "graduation-grey-sweater",
  // },
  // {
  //   feId: 17,
  //   bgIds: [16, 17],
  //   label: "Graduation, Black Sweater",
  //   value: "Graduation, Black Sweater",
  //   previewImgLastName: "graduation-black-sweater",
  // },
];
export const STYLE_BACKGROUNDS: StyleBackgroundProps[] = [
  {
    feId: 1,
    title: "Studio - Dark Grey",
    value: "Studio - Dark Gray", // value same as BE define
    previewImgFirstName: "dark-grey-studio",
    bgImg: images.BgStudioDarkGrey,
  },
  {
    feId: 2,
    title: "Studio - Light Grey",
    value: "Studio - Grey", // value same as BE define
    previewImgFirstName: "studio-grey",
    bgImg: images.BgStudioLightGrey,
  },
  {
    feId: 3,
    title: "Studio - White",
    value: "Studio - White", // value same as BE define
    previewImgFirstName: "white-studio",
    bgImg: images.BgStudioWhite,
  },
  {
    feId: 4,
    title: "Outdoor - City",
    value: "Outdoor - City",
    previewImgFirstName: "city",
    bgImg: images.BgOutdoorCity,
  },
  {
    feId: 5,
    title: "Outdoor - Garden",
    value: "Outdoor - Garden",
    previewImgFirstName: "garden",
    bgImg: images.BgOutdoorGarden,
  },
  {
    feId: 6,
    title: "Indoor - Office",
    value: "Indoor - Office",
    previewImgFirstName: "indoor-office",
    bgImg: images.BgIndoorOffice,
  },
  {
    feId: 7,
    title: "Indoor - Cafe",
    value: "Indoor - Cafe",
    previewImgFirstName: "cafe",
    bgImg: images.BgIndoorCafe,
  },
  {
    feId: 8,
    title: "Hospital",
    value: "Hospital",
    previewImgFirstName: "hospital",
    bgImg: images.BgHospital,
  },
  {
    feId: 9,
    title: "Studio - Aubergine",
    value: "Studio - Aubergine",
    previewImgFirstName: "studio-aubergine",
    bgImg: images.BgStudioAubergine,
  },
  {
    feId: 10,
    title: "Studio - Emerald",
    value: "Studio - Emerald",
    previewImgFirstName: "studio-emerald",
    bgImg: images.BgStudioEmerald,
  },
  {
    feId: 11,
    title: "Beach - Sunrise",
    value: "Beach - Sunrise",
    previewImgFirstName: "beach-sunrise",
    bgImg: images.BgBeachSunrise,
  },
  {
    feId: 12,
    title: "Beach - Sunset",
    value: "Beach - Sunset",
    previewImgFirstName: "beach-sunset",
    bgImg: images.BgBeachSunset,
  },
  {
    feId: 13,
    title: "Beach - Blue Hour",
    value: "Beach - Blue Hour",
    previewImgFirstName: "beach-blue-hour",
    bgImg: images.BgBeachBlueHour,
  },
  {
    feId: 14,
    title: "Birthday - Home",
    value: "Birthday - Home",
    previewImgFirstName: "birthday-home",
    bgImg: images.BgBirthdayHome,
  },
  {
    feId: 15,
    title: "Birthday - Studio",
    value: "Birthday - Studio",
    previewImgFirstName: "birthday-studio",
    bgImg: images.BgBirthdayStudio,
  },
  {
    feId: 16,
    title: "Graduation - Campus",
    value: "Graduation - Campus",
    previewImgFirstName: "graduation-campus",
    bgImg: images.BgUniversityCampus,
  },
  {
    feId: 17,
    title: "Graduation - Hallway",
    value: "Graduation - Hallway",
    previewImgFirstName: "graduation-hallway",
    bgImg: images.BgUniversityLibrary,
  },
  {
    feId: 18,
    title: "Studio - Concrete Wall",
    value: "Studio - Concrete Wall",
    previewImgFirstName: "studio-concrete-wall",
    bgImg: images.BgStudioConcreteWall,
  },
  {
    feId: 19,
    title: "Studio - Brick Wall",
    value: "Studio - Brick Wall",
    previewImgFirstName: "studio-brick-wall",
    bgImg: images.BgStudioBrickWall,
  },
  {
    feId: 20,
    title: "Studio - Dance",
    value: "Studio - Dance",
    previewImgFirstName: "studio-dance",
    bgImg: images.BgStudioDance,
  },
];
export const HEADSHOT_STYLES: HeadshotStyleProps[] = (() => {
  const res: HeadshotStyleProps[] = [];
  STYLE_BACKGROUNDS.forEach(sbg =>
    STYLE_CLOTHING.forEach(scl => {
      if (scl.bgIds.indexOf(sbg.feId) !== -1) {
        res.push({
          styleId: getStyleIdFromBgAndClothing(sbg.value, scl.value),
          maleImgName: camelCase(`${sbg.previewImgFirstName}-${scl.previewImgLastName}-male`) as keyof typeof images,
          femaleImgName: camelCase(
            `${sbg.previewImgFirstName}-${scl.previewImgLastName}-female`
          ) as keyof typeof images,
          bg: sbg,
          clothing: scl,
        });
      }
    })
  );
  return res;
})();
